import React, { useState } from 'react';
import ContactModal from 'src/components/ContactModal';
import ShareModal from 'src/components/ShareModal';
import {
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Icon,
  Link,
  Modal,
  ModalBody,
  Nav,
  NavItem,
  Row,
  toast,
  UncontrolledDropdown,
  useApp
} from 'straightline-ui';
import { isDispatched, isFTL, STATUS } from 'straightline-utils/constants';

// Note that there is a NavLink reactstrap item. We do not use it,
// but create our own that wraps a react-router Link
const NavLink = ({ to, active, children, ...rest }) => (
  <Link to={to} className={`nav-link ${active ? 'active' : ''}`} {...rest}>
    {children}
  </Link>
);

function CancelModal({ isOpen, toggle, shipment }) {
  const app = useApp();
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const requiresFee = isDispatched(shipment);
  const canCancel = shipment.status_change_options.includes(STATUS.CANCELLED);
  const fee = isFTL(shipment) ? 250 : 25;

  const handleClick = () => {
    if (!canCancel) {
      toast.errors('This shipment cannot be cancelled');
      return;
    }

    setIsLoading(true);

    return app
      .service('api/shipments')
      .patch(shipment.id, {
        status_type: STATUS.CANCELLED
      })
      .then((shipment) => {
        app.service('api/shipments').invalidateGet(shipment.id);
        toast.success('Shipment Cancelled');
        toggle();

        app
          .service('api/contact')
          .create({
            emails: [process.env.REACT_APP_SUPPORT_EMAIL],
            service_id: shipment.id,
            service: 'api/shipments',
            subject: `Shipment #${shipment.id} Cancelled`,
            text: message
          })
          .catch((error) => {
            app.get('sentryClient').captureException(error);
          });
      })
      .catch((error) => {
        toast.errors(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      className="mx-auto"
      centered
      style={{ maxWidth: 425, width: '95%' }}
    >
      {() => {
        if (!canCancel) {
          return (
            <ModalBody>
              <h2>Cancel Shipment</h2>
              <p>This shipment cannot be cancelled.</p>
              <Button block color="white" onClick={toggle}>
                Close
              </Button>
            </ModalBody>
          );
        }

        return (
          <ModalBody>
            <h2>Cancel Shipment</h2>
            {requiresFee ? (
              <div>
                <p>
                  Your shipment has already been dispatched. Canceling the
                  shipment will incur a <strong>Truck Order Not Used</strong>{' '}
                  fee of&nbsp;
                  <strong>${fee}</strong>.
                </p>
                <p>Are you sure you would like to cancel the shipment?</p>
              </div>
            ) : (
              <p>
                Are you sure you would like to cancel the shipment? This cannot
                be undone.
              </p>
            )}
            <div>
              <textarea
                placeholder="Why are you cancelling?"
                className="form-control"
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
            <div className="mt-4">
              <Button
                block
                color="primary"
                onClick={handleClick}
                disabled={!message || isLoading}
                className={isLoading ? 'is-loading' : ''}
              >
                Cancel Shipment
              </Button>
              <Button block color="white" onClick={toggle} disabled={isLoading}>
                Do not cancel Shipment
              </Button>
            </div>
          </ModalBody>
        );
      }}
    </Modal>
  );
}

export default function ShipmentPageHeader({ shipment, activeTab }) {
  const canCancel = shipment.status_change_options.includes(STATUS.CANCELLED);

  const [contactModalOpen, setContactModalOpen] = useState(false);
  const toggleContactModal = () => setContactModalOpen(!contactModalOpen);

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const toggleShareModal = () => setShareModalOpen(!shareModalOpen);

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const toggleCancelModal = () => setCancelModalOpen(!cancelModalOpen);

  return (
    <React.Fragment>
      <div className="header">
        <div className="header-body">
          <Row className="font-weight-bold">
            <Col xs="auto" className="mb-2">
              <span className="text-muted">SL#: </span>
              {shipment.id}
            </Col>
            {shipment.reference_number && (
              <Col xs="auto" className="mb-2">
                <span className="text-muted">REF#: </span>
                {shipment.reference_number}
              </Col>
            )}
            {shipment.shipment_dispatch?.carrier_pro_number && (
              <Col xs="auto" className="mb-2">
                <span className="text-muted">PRO#: </span>
                {shipment.shipment_dispatch.carrier_pro_number}
              </Col>
            )}
            {shipment.mode && (
              <Col xs="auto" className="mb-2">
                <span className="text-muted">MODE: </span>
                {shipment.mode}
              </Col>
            )}
            {shipment.carrier && (
              <Col xs="auto" className="mb-2">
                <span className="text-muted">CARRIER: </span>
                {shipment.carrier.name}
              </Col>
            )}
            {shipment.rate && (
              <Col xs="auto" className="mb-2">
                <span className="text-muted">SERVICE: </span>
                {shipment.rate.service_level.name}
              </Col>
            )}
          </Row>
          <Row className="align-items-end justify-content-between mt-md-n3">
            <Col xs="auto">
              <Row className="align-items-center mb-n3">
                <Col xs="auto" className="mb-2 pr-0 align-items-center d-flex">
                  <h1 className="mb-0 font-weight-bold d-inline-block mr-3">{`${shipment.pickup_address.city}, ${shipment.pickup_address.state}`}</h1>
                  <Icon
                    type="arrow-right"
                    style={{ fontSize: 24 }}
                    className="text-success lead d-inline-block font-weight-bold"
                  />
                </Col>
                <Col xs="auto" className="mb-2">
                  <h1 className="mb-0 font-weight-bold d-inline-block">{`${shipment.delivery_address.city}, ${shipment.delivery_address.state}`}</h1>
                </Col>
              </Row>
            </Col>
            <Col xs="auto" className="text-right">
              <Nav className="nav nav-tabs header-tabs">
                <NavItem>
                  <NavLink
                    active={activeTab === 'details'}
                    to={`/shipments/${shipment.id}`}
                  >
                    Details
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    active={activeTab === 'documents'}
                    to={`/shipments/${shipment.id}?tab=documents`}
                  >
                    Documents
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    active={activeTab === 'bol'}
                    to={`/shipments/${shipment.id}?tab=bol`}
                  >
                    BOL
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    active={activeTab === 'invoice'}
                    to={`/shipments/${shipment.id}?tab=invoice`}
                  >
                    Invoice
                  </NavLink>
                </NavItem>
                <NavItem>
                  <UncontrolledDropdown>
                    <DropdownToggle caret nav>
                      Actions
                    </DropdownToggle>
                    <DropdownMenu right className="shadow">
                      <DropdownItem onClick={toggleShareModal}>
                        <div className="d-flex">
                          <div className="mr-3">
                            <Icon type="share" style={{ fontSize: '1.1rem' }} />
                          </div>
                          <div>
                            <div className="font-weight-bold">
                              Share Shipment
                            </div>
                            <div className="small">
                              Share this shipment via link, email, or text.
                            </div>
                          </div>
                        </div>
                      </DropdownItem>
                      <DropdownItem divider className="my-1" />
                      <Link
                        to="/quote"
                        query={{ copied_shipment_id: shipment.id }}
                        className="dropdown-item"
                      >
                        <div className="d-flex">
                          <div className="mr-3">
                            <Icon
                              type="repeat"
                              style={{ fontSize: '1.1rem' }}
                            />
                          </div>
                          <div>
                            <div className="font-weight-bold">
                              Repeat Shipment
                            </div>
                            <div className="small">
                              Get a new quote reusing this shipment's data
                            </div>
                          </div>
                        </div>
                      </Link>
                      <DropdownItem divider className="my-1" />
                      <DropdownItem onClick={toggleContactModal}>
                        <div className="d-flex">
                          <div className="mr-3">
                            <Icon
                              type="phone-outgoing"
                              style={{ fontSize: '1.1rem' }}
                            />
                          </div>
                          <div>
                            <div className="font-weight-bold">
                              Contact Straightline
                            </div>
                            <div className="small">
                              Call or email us with any questions
                            </div>
                          </div>
                        </div>
                      </DropdownItem>
                      {canCancel && (
                        <React.Fragment>
                          <DropdownItem divider className="my-1" />
                          <DropdownItem onClick={toggleCancelModal}>
                            <div className="d-flex">
                              <div className="mr-3">
                                <Icon
                                  type="slash"
                                  style={{ fontSize: '1.1rem' }}
                                />
                              </div>
                              <div>
                                <div className="font-weight-bold">
                                  Cancel Shipment
                                </div>
                                <div className="small">
                                  This cannot be undone
                                </div>
                              </div>
                            </div>
                          </DropdownItem>
                        </React.Fragment>
                      )}
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </NavItem>
              </Nav>
            </Col>
          </Row>
        </div>
      </div>
      <ContactModal
        isOpen={contactModalOpen}
        toggle={toggleContactModal}
        config={{
          subject: `App Contact - Shipment #${shipment.id}`,
          service: 'api/shipments',
          service_id: shipment.id
        }}
      />
      <ShareModal
        isOpen={shareModalOpen}
        toggle={toggleShareModal}
        shipment={shipment}
      />
      <CancelModal
        isOpen={cancelModalOpen}
        toggle={toggleCancelModal}
        shipment={shipment}
      />
    </React.Fragment>
  );
}
